<template>
  <div class="category-column">
    <!-- 时间轴容器 -->
    <div class="timeline-container">
      <!-- 时间轴虚线箭头 -->
      <div class="timeline-arrow" :style="{ borderColor: getBorderColor(category.label) }">
        <div class="arrow-head" :style="{ borderBottomColor: getBorderColor(category.label) }"></div>
      </div>
      
      <!-- 词条柱体 -->
      <div 
        class="keywords-column"
        :class="{ 'related-category': isRelated }"
        :style="{ 
          height: columnHeight,
          backgroundColor: category.labelColor || '#E8F5E8',
          boxShadow: `0 2px 8px ${getBorderColor(category.label)}30`
        }"
        @click="onCategoryClick"
      >
        <!-- 柱体顶部计数 -->
        <div class="column-header">
          {{ keywordsCount }}
        </div>
        
        <!-- 词条列表 -->
        <div class="keywords-list">
          <div 
            v-for="keyword in keywords"
            :key="keyword.book_keywords_id"
            :ref="`keyword-${keyword.book_keywords_id}`"
            class="keyword-item"
            :class="{ 'highlighted': isHighlighted(keyword.keywords) }"
            @click.stop="onKeywordClick(keyword, $event)"
            :title="keyword.keywords"
          >
            {{ keyword.keywords }}
          </div>
          
          <!-- 加载更多按钮 -->
          <div 
            v-if="hasMore && !loading"
            class="load-more"
          >
            <button 
              class="load-more-btn"
              @click.stop="loadMore"
            >
              更多
            </button>
          </div>
          
          <!-- 加载状态 -->
          <div 
            v-if="loading"
            class="load-more"
          >
            <span class="load-more-btn">...</span>
          </div>
        </div>
        
        <!-- 时间轴原点圆形图标 - 移到柱体内部底部 -->
        <div class="timeline-origin" :class="getCategoryStyleClass()" :style="{ borderColor: '#555555' }">
        </div>
      </div>
    </div>
    
    <!-- 分类名称 - 位于柱体下方，只显示中文 -->
    <div class="category-name" :title="category.classify_name">
      {{ getChineseName(category.classify_name) }}
    </div>
    
    <!-- 词条描述弹窗 -->
    <div 
      v-if="showPopover && selectedKeyword"
      class="keyword-popover"
      :style="popoverStyle"
    >
      <div class="popover-header">
        <h4>{{ selectedKeyword.keywords }}</h4>
        <button class="close-btn" @click="closePopover">×</button>
      </div>
      <div class="popover-content">
        <div v-if="getKeywordDescriptions().length > 0">
          <div 
            v-for="(desc, index) in getKeywordDescriptions()" 
            :key="index"
            class="description-item"
          >
            <p>{{ desc.description }}</p>
            <div v-if="desc.book" class="book-info">
              <span class="book-name">来源：{{ desc.book.book_name }}</span>
            </div>
          </div>
        </div>
        <p v-else class="no-description">暂无描述信息</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CategoryColumn',
  props: {
    category: {
      type: Object,
      required: true
    },
    keywords: {
      type: Array,
      default: () => []
    },
    keywordsCount: {
      type: Number,
      default: 0
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isRelated: {
      type: Boolean,
      default: false
    },
    highlightKeywords: {
      type: Array,
      default: () => []
    },
    categoryIndex: {
      type: Number,
      default: 0
    },
    activePopoverCategoryId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      showPopover: false,
      selectedKeyword: null,
      popoverStyle: {}
    }
  },
  computed: {
    columnHeight() {
      return 'calc(100vh - 200px)'
    }
  },
  watch: {
    // 监听全局激活的弹窗分类ID
    activePopoverCategoryId(newVal) {
      // 如果激活的不是当前分类，关闭当前弹窗
      if (newVal !== null && newVal !== this.category.classify_id && this.showPopover) {
        this.closePopover()
      }
    }
  },
  mounted() {
    // 点击页面其他地方关闭弹窗
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    onCategoryClick() {
      this.$emit('category-click', this.category)
    },
    
    onKeywordClick(keyword, event) {
      // 阻止冒泡
      event.stopPropagation()
      
      // 如果点击的是同一个词条，关闭弹窗
      if (this.selectedKeyword && this.selectedKeyword.book_keywords_id === keyword.book_keywords_id) {
        this.closePopover()
        return
      }
      
      // 设置选中的词条
      this.selectedKeyword = keyword
      this.showPopover = true
      
      // 计算弹窗位置
      this.$nextTick(() => {
        this.calculatePopoverPosition(event.target)
      })
      
      // 触发父组件事件，传递分类ID
      this.$emit('keyword-click', keyword, this.category.classify_id)
    },
    
    // 计算弹窗位置
    calculatePopoverPosition(targetElement) {
      const rect = targetElement.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const popoverWidth = 300 // 弹窗宽度
      const popoverMaxHeight = 400 // 弹窗最大高度
      
      // 默认显示在右侧
      let left = rect.right + 10
      let top = rect.top
      
      // 如果右侧空间不够，显示在左侧
      if (left + popoverWidth > viewportWidth) {
        left = rect.left - popoverWidth - 10
      }
      
      // 如果左侧空间也不够，显示在词条下方
      if (left < 0) {
        left = rect.left
        top = rect.bottom + 10
        
        // 如果下方空间不够，显示在上方
        if (top + popoverMaxHeight > viewportHeight) {
          top = rect.top - popoverMaxHeight - 10
        }
      }
      
      // 确保弹窗不超出视口顶部和底部
      if (top < 10) {
        top = 10
      }
      if (top + popoverMaxHeight > viewportHeight - 10) {
        top = viewportHeight - popoverMaxHeight - 10
      }
      
      this.popoverStyle = {
        left: `${left}px`,
        top: `${top}px`
      }
    },
    
    // 关闭弹窗
    closePopover() {
      this.showPopover = false
      this.selectedKeyword = null
      this.popoverStyle = {}
    },
    
    // 处理点击外部关闭弹窗
    handleClickOutside(event) {
      if (this.showPopover) {
        const popover = this.$el.querySelector('.keyword-popover')
        const keywordItems = this.$el.querySelectorAll('.keyword-item')
        
        // 检查点击是否在弹窗内或词条项上
        let clickedInside = false
        if (popover && popover.contains(event.target)) {
          clickedInside = true
        }
        keywordItems.forEach(item => {
          if (item.contains(event.target)) {
            clickedInside = true
          }
        })
        
        if (!clickedInside) {
          this.closePopover()
        }
      }
    },
    
    loadMore() {
      this.$emit('load-more', this.category.classify_id)
    },
    
    // 提取中文名称
    getChineseName(fullName) {
      if (!fullName) return ''
      // 匹配中文字符，去掉英文部分
      const chineseMatch = fullName.match(/[\u4e00-\u9fa5]+/g)
      if (chineseMatch) {
        return chineseMatch.join('')
      }
      return fullName
    },
    
    // 根据标签获取边框颜色（比背景色深一些）
    getBorderColor(label) {
      const borderColors = {
        '1': '#9575CD',  // 哲学门类 - 紫色
        '2': '#FFB74D',  // 经济学门类 - 橙色
        '3': '#EC407A',  // 法学门类 - 粉红色
        '4': '#66BB6A',  // 教育学门类 - 绿色
        '5': '#81C784',  // 文学门类 - 浅绿色
        '6': '#FDD835',  // 历史学门类 - 黄色
        '7': '#BA68C8',  // 理学门类 - 淡紫色
        '8': '#42A5F5',  // 工学门类 - 蓝色
        '9': '#F48FB1',  // 农学门类 - 浅粉色
        '10': '#AED581', // 医学门类 - 浅绿色
        '11': '#FFB74D', // 管理学门类 - 橙色
        '12': '#A1887F'  // 艺术学门类 - 褐色
      }
      return borderColors[label] || borderColors['1']
    },
    
    // 判断关键词是否需要高亮
    isHighlighted(keywordText) {
      // 去除首尾空格后进行比较
      const trimmedKeyword = keywordText ? keywordText.trim() : ''
      return this.highlightKeywords.some(hk => {
        const trimmedHk = hk ? hk.trim() : ''
        return trimmedHk === trimmedKeyword
      })
    },
    
    // 根据分类索引获取圆球样式类（循环使用1-10）
    getCategoryStyleClass() {
      const styleIndex = (this.categoryIndex % 10) + 1
      return `category-${styleIndex}`
    },
    
    // 获取词条描述列表
    getKeywordDescriptions() {
      if (!this.selectedKeyword) {
        return []
      }
      
      // 如果 description_all 是数组，直接返回
      if (Array.isArray(this.selectedKeyword.description_all)) {
        return this.selectedKeyword.description_all
      }
      
      // 如果 description_all 是字符串，尝试解析为JSON
      if (typeof this.selectedKeyword.description_all === 'string') {
        try {
          const parsed = JSON.parse(this.selectedKeyword.description_all)
          if (Array.isArray(parsed)) {
            return parsed
          }
        } catch (e) {
          console.error('解析 description_all 失败:', e)
        }
      }
      
      return []
    }
  }
}
</script>

<style scoped>
/* 组件样式已在主CSS文件中定义 */
</style>
